/**
 * ShortcodeEmbed — render one CMS-wide shortkey embed on the public site.
 *
 * Editors manage shortkeys at /admin/cms/embeds (`key` → target module +
 * row). On the public page, `<ShortcodeEmbed shortkey="gallery-2026-dinner" />`
 * resolves it and renders the target.
 *
 * HOW A TARGET IS RESOLVED (two requests, on purpose)
 * -----------------------------------------------------
 *   1. `GET /api/cms/public/embeds/:key` → `{ target: { kind, slug } }`.
 *      The registry knows the module and the slug; it does NOT re-send the
 *      content.
 *   2. `GET /api/cms/public/<module>/:slug>?locale=…` → the module's own
 *      public payload.
 *
 * The second request is the point: that endpoint already knows how to pick
 * the locale, fall back to zh-Hant, run OpenCC for a Simplified reader,
 * assemble template placeholders and hide drafts. If this component rebuilt
 * that, there would be two copies of those rules, and they would drift.
 *
 * So an embed of an article shows the article's real title, its real body and
 * its real images, in the reader's language — and a gallery shows its photos
 * with their captions, in the gallery's own display mode.
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { GalleryPhotos } from '../gallery/PhotoGrid'
import { CmsImage } from '../media/CmsImage'
import EmbedBody from './EmbedBody'
import {
  embedTargetHref,
  embedTargetInfo,
  type EmbedTargetType,
} from '@/cms/embed-targets'
import { pickLocalized } from '@/cms/pickLocale'
import type { Locale } from '@/utils/localePath'

interface ShortcodeEmbedProps {
  shortkey: string
  className?: string
}

/** What step 1 returns. */
interface EmbedPointer {
  kind: EmbedTargetType
  slug: string
}

/** The module payloads the embeds render from. */
type ModulePayload =
  | { kind: 'gallery'; gallery: GalleryPayload }
  | { kind: 'article'; article: ArticlePayload }
  | { kind: 'lecture'; lecture: LecturePayload }
  | { kind: 'magazine'; magazine: MagazinePayload }
  | { kind: 'product_page'; product_page: ProductPagePayload }

/** The gallery endpoint returns its copy already collapsed to one locale. */
interface GalleryPayload {
  slug: string
  title: string
  description: string
  event_date: string | null
  cover_media_uuid: string | null
  display_mode: string
  photos: Array<{ media_uuid: string; caption?: string }>
}

interface ArticlePayload {
  slug: string
  title: string
  excerpt: string
  body_md: string
  cover_media_uuid: string | null
}

interface LecturePayload {
  slug: string
  title_zhHant: string
  title_zhHans: string
  title_en: string
  summary_zhHant: string
  summary_zhHans: string
  summary_en: string
  body_md: string
}

interface MagazinePayload {
  slug: string
  title_zhHant: string
  title_zhHans: string
  title_en: string
  summary_zhHant: string
  summary_zhHans: string
  summary_en: string
  cover_media_uuid: string | null
}

interface ProductPagePayload {
  product_slug: string
  subtitle: string
  summary: string
  hero_uuid: string | null
}

export default function ShortcodeEmbed({ shortkey, className }: ShortcodeEmbedProps) {
  const { i18n } = useTranslation()
  const locale = (i18n.language || 'zh-Hant') as 'zh-Hant' | 'zh-Hans' | 'en'
  const [pointer, setPointer] = useState<EmbedPointer | null>(null)
  const [payload, setPayload] = useState<ModulePayload | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Step 1 — the registry: which module, which slug.
  useEffect(() => {
    let cancelled = false
    setError(null)
    setPointer(null)
    setPayload(null)
    fetch(`/api/cms/public/embeds/${encodeURIComponent(shortkey)}`)
      .then((r) => {
        if (!r.ok) throw new Error(`短鍵 ${shortkey} 不存在或未啟用`)
        return r.json() as Promise<{ target: EmbedPointer }>
      })
      .then((j) => {
        if (cancelled) return
        setPointer(j.target)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : '嵌入載入失敗')
      })
    return () => {
      cancelled = true
    }
  }, [shortkey])

  // Step 2 — the module's own public payload, in the reader's language.
  useEffect(() => {
    if (!pointer) return
    let cancelled = false
    const info = embedTargetInfo(pointer.kind)
    fetch(`${info.apiPath(pointer.slug)}?locale=${locale}`)
      .then((r) => {
        if (!r.ok) throw new Error(`無法載入${info.label}內容`)
        return r.json() as Promise<Record<string, unknown>>
      })
      .then((j) => {
        if (cancelled) return
        setPayload(normalisePayload(pointer.kind, j))
      })
      .catch((err) => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : '嵌入內容載入失敗')
      })
    return () => {
      cancelled = true
    }
  }, [pointer, locale])

  if (error) {
    return (
      <div className={className ?? ''} role="alert">
        <p className="rounded-yada-md bg-danger/10 border border-danger/20 px-4 py-3 text-sm text-danger">
          無法載入短鍵「{shortkey}」：{error}
        </p>
      </div>
    )
  }
  if (!payload || !pointer) return null

  const href = embedTargetHref(pointer.kind, pointer.slug, locale)
  const info = embedTargetInfo(pointer.kind)

  return (
    <section
      className={className ?? ''}
      aria-label={`內嵌${info.label}`}
      data-embed-kind={pointer.kind}
    >
      {payload.kind === 'gallery' && (
        <GalleryEmbed data={payload.gallery} href={href} />
      )}
      {payload.kind === 'article' && (
        <ArticleEmbed data={payload.article} href={href} />
      )}
      {payload.kind === 'lecture' && (
        <LectureEmbed data={payload.lecture} href={href} locale={locale} />
      )}
      {payload.kind === 'magazine' && (
        <MagazineEmbed data={payload.magazine} href={href} locale={locale} />
      )}
      {payload.kind === 'product_page' && (
        <ProductPageEmbed data={payload.product_page} href={href} />
      )}
    </section>
  )
}

/**
 * Fold each module's own wire shape into the one shape the renderers take.
 *
 * The gallery endpoint returns the photos as a SIBLING of the gallery object
 * (`{ gallery: {…}, photos: […] }`) — that is the shape the gallery page and
 * the build-time snapshot already read, so the endpoint is not changed here.
 * Every other module returns one object under its own key (`{ article: {…} }`,
 * `{ lecture: {…} }`, …).
 *
 * So the adapter puts the gallery's photos back INSIDE the gallery object, and
 * all five renderers can then take one shape. Without this the gallery branch
 * read `data.photos.length` on `undefined` and took the whole page down.
 */
function normalisePayload(
  kind: EmbedTargetType,
  j: Record<string, unknown>,
): ModulePayload {
  switch (kind) {
    case 'gallery': {
      const gallery = (j['gallery'] ?? {}) as GalleryPayload
      const photos = (j['photos'] ?? []) as GalleryPayload['photos']
      return { kind: 'gallery', gallery: { ...gallery, photos } }
    }
    case 'article':
      return { kind: 'article', article: (j['article'] ?? {}) as ArticlePayload }
    case 'lecture':
      return { kind: 'lecture', lecture: (j['lecture'] ?? {}) as LecturePayload }
    case 'magazine':
      return { kind: 'magazine', magazine: (j['magazine'] ?? {}) as MagazinePayload }
    case 'product_page':
      return {
        kind: 'product_page',
        product_page: (j['product_page'] ?? {}) as ProductPagePayload,
      }
    default:
      throw new Error(`unknown embed target type: ${kind}`)
  }
}

/** A gallery: its photos, in its own display mode, with the captions. */
function GalleryEmbed({ data, href }: { data: GalleryPayload; href: string }) {
  const { t } = useTranslation()
  const title = data.title
  const description = data.description
  const layout = (data.display_mode ?? 'grid') as 'grid' | 'slider' | 'list'

  return (
    <>
      {title && <EmbedHeading title={title} href={href} />}
      {description && <p className="text-sm text-ink-muted mb-5">{description}</p>}
      {data.photos.length > 0 ? (
        <GalleryPhotos
          photos={data.photos.map((p) => ({ uuid: p.media_uuid, caption: p.caption }))}
          layout={layout}
        />
      ) : (
        <p className="text-ink-muted">{t('gallery.emptyPhotos')}</p>
      )}
    </>
  )
}

/** An article: its title and its body, inline. */
function ArticleEmbed({ data, href }: { data: ArticlePayload; href: string }) {
  return (
    <>
      <EmbedHeading title={data.title} href={href} />
      {data.excerpt && <p className="text-sm text-ink-muted mb-5">{data.excerpt}</p>}
      <EmbedBody md={data.body_md} />
    </>
  )
}

/** A lecture: its title, its summary and its body. */
function LectureEmbed({
  data,
  href,
  locale,
}: {
  data: LecturePayload
  href: string
  locale: Locale
}) {
  const title = pickLocalized(locale, data.title_zhHans, data.title_zhHant, data.title_en)
  const summary = pickLocalized(
    locale,
    data.summary_zhHans,
    data.summary_zhHant,
    data.summary_en,
  )
  return (
    <>
      <EmbedHeading title={title} href={href} />
      {summary && <p className="text-sm text-ink-muted mb-5">{summary}</p>}
      <EmbedBody md={data.body_md} />
    </>
  )
}

/** A magazine piece: a card — it has no inline body to place. */
function MagazineEmbed({
  data,
  href,
  locale,
}: {
  data: MagazinePayload
  href: string
  locale: Locale
}) {
  const title = pickLocalized(locale, data.title_zhHans, data.title_zhHant, data.title_en)
  const summary = pickLocalized(
    locale,
    data.summary_zhHans,
    data.summary_zhHant,
    data.summary_en,
  )
  return (
    <Link
      to={href}
      className="group flex items-start gap-4 rounded-yada-lg border border-surface-3 bg-surface-2 p-4 transition-colors hover:border-primary"
    >
      {data.cover_media_uuid && (
        <CmsImage
          uuid={data.cover_media_uuid}
          sizes="120px"
          className="size-24 shrink-0 rounded-yada-md object-cover border border-border"
        />
      )}
      <span className="min-w-0">
        <span className="block font-semibold text-ink group-hover:text-primary">
          {title}
        </span>
        {summary && (
          <span className="mt-1 block line-clamp-3 text-sm text-ink-muted">{summary}</span>
        )}
      </span>
    </Link>
  )
}

/** A product page: its subtitle, its summary and its hero image. */
function ProductPageEmbed({
  data,
  href,
}: {
  data: ProductPagePayload
  href: string
}) {
  return (
    <Link
      to={href}
      className="group flex items-start gap-4 rounded-yada-lg border border-surface-3 bg-surface-2 p-4 transition-colors hover:border-primary"
    >
      {data.hero_uuid && (
        <CmsImage
          uuid={data.hero_uuid}
          sizes="120px"
          className="size-24 shrink-0 rounded-yada-md object-cover border border-border"
        />
      )}
      <span className="min-w-0">
        <span className="block font-semibold text-ink group-hover:text-primary">
          {data.subtitle}
        </span>
        {data.summary && (
          <span className="mt-1 block line-clamp-3 text-sm text-ink-muted">
            {data.summary}
          </span>
        )}
      </span>
    </Link>
  )
}

/** The shared heading: the target's title, linking to the target's own page. */
function EmbedHeading({ title, href }: { title: string; href: string }) {
  return (
    <h3 className="text-lg font-bold text-ink mb-3">
      <Link to={href} className="hover:text-primary transition-colors">
        {title}
      </Link>
    </h3>
  )
}
